/**
 * IntrospectWizard — Mapping-sheet creation wizard with four modes:
 *
 * - "ai": Multi-step AI-assisted flow (unchanged from Story 6.6).
 *     Step 1 (idle): Optional table name input + "Introspect Source" button.
 *     Step 2 (introspecting): Spinner + polling until task completed/failed.
 *     Step 3 (schema): Schema tree — table names + column names/types only.
 *       "Propose Jobs with AI" button + intent prompt input.
 *     Step 4 (proposing): Spinner + polling until proposal completed/failed.
 * - "auto": Deterministic fuzzy-match auto-mapping (Story 6.7). Introspects
 *     BOTH source and target schemas, then calls the auto-mapper — no LLM,
 *     synchronous result.
 * - "upload": Manual CSV/XLSX mapping sheet upload (Story 6.7). Accepts
 *     either the canonical snake_case columns or Data Converter's own
 *     "Generate STTM" export headers (see importMapping/pe-client.ts).
 *     Jobs are created automatically as soon as the file parses
 *     successfully — no separate review/Accept step for this mode.
 * - "converter": Reuses a Data Converter project's already-finalized
 *     source-to-target mapping (Story 6.7).
 *
 * The "ai", "auto", and "converter" modes converge on the same
 * <ProposalTable> for accept/reject, since each produces the identical
 * PEProposal.items shape. "upload" bypasses it (see above).
 *
 * Safety: renders only schema metadata (names, types). No data values are
 * displayed at any step — introspection returns schema metadata exclusively.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Loader2,
  Search,
  Sparkles,
  X,
  ChevronRight,
  ChevronDown,
  Upload,
  FolderInput,
} from "lucide-react";
import type {
  SchemaTable,
  ProposedJobItem,
  PEProposal,
  ProposalStatus,
} from "../../features/pipeline-engineer/types";
import { apiv2, type ProjectListItem } from "../../apiv2";
import {
  useIntrospectTask,
  useProposeJob,
} from "../../features/pipeline-engineer/useAgenticApi";
import {
  acceptProposal,
  importMapping,
  listDataConverterMappingVersions,
  mappingFromDataConverter,
  type DataConverterMappingVersion,
} from "../../features/pipeline-engineer/api/pe-client";
import { ProposalTable } from "./ProposalTable";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INPUT_CLASS =
  "w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30";

const LABEL_CLASS = "block text-xs font-medium text-white/60 mb-1";

type MappingMode = "ai" | "upload" | "converter";

const MODE_TABS: Array<{
  id: MappingMode;
  label: string;
  icon: typeof Sparkles;
}> = [
  { id: "ai", label: "AI Propose", icon: Sparkles },
  { id: "upload", label: "Upload Mapping", icon: Upload },
  { id: "converter", label: "From Data Converter", icon: FolderInput },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface IntrospectWizardProps {
  pipelineId: string;
  projectId: string;
  onJobsAccepted: (jobsCreated: number) => void;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Schema tree sub-components (schema metadata only — names/types, zero data)
// ---------------------------------------------------------------------------

interface SchemaTableTreeProps {
  table: SchemaTable;
}

function SchemaTableTree({ table }: SchemaTableTreeProps) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="border border-white/10 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/8 transition-colors text-left"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown
            size={13}
            className="text-white/40 flex-shrink-0"
            aria-hidden="true"
          />
        ) : (
          <ChevronRight
            size={13}
            className="text-white/40 flex-shrink-0"
            aria-hidden="true"
          />
        )}
        <span className="text-xs font-mono font-semibold text-white/80">
          {table.name}
        </span>
        <span className="text-xs text-white/30 ml-auto">
          {table.columns.length} cols
        </span>
      </button>
      {expanded && (
        <div className="border-t border-white/10 divide-y divide-white/5">
          {table.columns.map((col) => (
            <div key={col.name} className="flex items-center gap-3 px-4 py-1.5">
              <span className="text-xs font-mono text-white/70 flex-1 truncate">
                {col.name}
              </span>
              <span className="text-xs text-white/40 flex-shrink-0">
                {col.type}
              </span>
              {col.isPk && (
                <span className="text-xs text-amber-400/70 flex-shrink-0 font-medium">
                  PK
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function IntrospectWizard({
  pipelineId,
  projectId,
  onJobsAccepted,
  onClose,
}: IntrospectWizardProps) {
  const [mode, setMode] = useState<MappingMode>("ai");

  // ── Mode "ai" state — unchanged from Story 6.6 ──────────────────────────
  const [tableNamesRaw, setTableNamesRaw] = useState("");
  const [userPrompt, setUserPrompt] = useState("");
  const [promptError, setPromptError] = useState<string | null>(null);

  const introspect = useIntrospectTask(pipelineId, projectId, "source");
  const propose = useProposeJob(pipelineId, projectId);

  const handleIntrospect = useCallback(async () => {
    const tableNames = tableNamesRaw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    await introspect.start(tableNames.length > 0 ? tableNames : null);
  }, [tableNamesRaw, introspect]);

  const handlePropose = useCallback(async () => {
    setPromptError(null);
    if (!userPrompt.trim()) {
      setPromptError("Please describe your intent before proposing jobs.");
      return;
    }
    const taskId = introspect.task?.taskId;
    if (taskId === undefined || taskId === null || taskId === "") return;
    await propose.start(taskId, userPrompt.trim());
  }, [userPrompt, introspect.task, propose]);

  const introspectDone =
    introspect.task !== null && introspect.task.status === "completed";
  const proposalDone =
    propose.proposal !== null &&
    propose.proposal.taskStatus === "completed" &&
    propose.proposal.items !== null;

  const schemaTables: SchemaTable[] = introspect.task?.result?.tables ?? [];

  // ── Mode "upload" state — Story 6.7 ──────────────────────────────────────
  // Uploads auto-create jobs immediately on a successful parse (no manual
  // Accept step) — the review-and-accept ProposalTable is skipped for this
  // mode entirely; see handleUpload.
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<{
    jobsCreated: number;
    jobsSkipped: number;
  } | null>(null);

  const handleUpload = useCallback(async () => {
    if (uploadFile === null) {
      setUploadError("Please choose a CSV or XLSX mapping file first.");
      return;
    }
    setUploadLoading(true);
    setUploadError(null);
    try {
      const data = await importMapping(pipelineId, uploadFile);
      const accepted = await acceptProposal(data.proposalId);
      setUploadResult({
        jobsCreated: accepted.jobsCreated,
        jobsSkipped: accepted.jobsSkipped,
      });
      onJobsAccepted(accepted.jobsCreated);
    } catch (err) {
      setUploadError(
        err instanceof Error ? err.message : "Failed to import mapping file.",
      );
    } finally {
      setUploadLoading(false);
    }
  }, [pipelineId, uploadFile, onJobsAccepted]);

  // ── Mode "converter" state — Story 6.7 ───────────────────────────────────
  const [converterProjects, setConverterProjects] = useState<ProjectListItem[]>(
    [],
  );
  const [converterProjectsLoading, setConverterProjectsLoading] =
    useState(false);
  const [selectedConverterProjectId, setSelectedConverterProjectId] =
    useState("");
  const [converterVersions, setConverterVersions] = useState<
    DataConverterMappingVersion[]
  >([]);
  const [converterVersionsLoading, setConverterVersionsLoading] =
    useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState("");
  const [converterLoading, setConverterLoading] = useState(false);
  const [converterError, setConverterError] = useState<string | null>(null);
  const [converterProposal, setConverterProposal] = useState<
    (PEProposal & { items: ProposedJobItem[] }) | null
  >(null);

  useEffect(() => {
    if (mode !== "converter") return;
    let cancelled = false;
    setConverterProjectsLoading(true);
    apiv2
      .listProjects()
      .then((data) => {
        if (!cancelled) setConverterProjects(data);
      })
      .catch(() => {
        if (!cancelled) setConverterProjects([]);
      })
      .finally(() => {
        if (!cancelled) setConverterProjectsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  // Every saved mapping version for the selected project, so the user can
  // reuse an older save instead of only ever the latest one.
  useEffect(() => {
    if (!selectedConverterProjectId) {
      setConverterVersions([]);
      setSelectedHistoryId("");
      return;
    }
    let cancelled = false;
    setConverterVersionsLoading(true);
    listDataConverterMappingVersions(selectedConverterProjectId)
      .then((versions) => {
        if (cancelled) return;
        setConverterVersions(versions);
        setSelectedHistoryId(versions[0]?.historyId ?? "");
      })
      .catch(() => {
        if (!cancelled) setConverterVersions([]);
      })
      .finally(() => {
        if (!cancelled) setConverterVersionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedConverterProjectId]);

  const handleReuseFromConverter = useCallback(async () => {
    if (!selectedConverterProjectId) {
      setConverterError("Please select a Data Converter project first.");
      return;
    }
    setConverterLoading(true);
    setConverterError(null);
    try {
      const data = await mappingFromDataConverter(
        pipelineId,
        selectedConverterProjectId,
        selectedHistoryId || null,
      );
      setConverterProposal({
        proposalId: data.proposalId,
        status: data.status as ProposalStatus,
        taskStatus: "completed",
        items: data.items,
        error: null,
      });
    } catch (err) {
      setConverterError(
        err instanceof Error
          ? err.message
          : "Failed to reuse Data Converter mapping.",
      );
    } finally {
      setConverterLoading(false);
    }
  }, [pipelineId, selectedConverterProjectId, selectedHistoryId]);

  // ── Mode switching — resets every mode's state so stale results from a
  // previously-selected mode never leak into the newly-selected one. ──────
  const handleModeChange = useCallback(
    (newMode: MappingMode) => {
      setMode(newMode);
      introspect.reset();
      propose.reset();
      setTableNamesRaw("");
      setUserPrompt("");
      setPromptError(null);
      setUploadFile(null);
      setUploadResult(null);
      setUploadError(null);
      setConverterProposal(null);
      setConverterError(null);
      setSelectedConverterProjectId("");
      setConverterVersions([]);
      setSelectedHistoryId("");
    },
    [introspect, propose],
  );

  // ── Unified proposal — whichever mode's pathway has a finished result.
  // "upload" is intentionally excluded: it auto-creates jobs immediately on
  // successful parse (see handleUpload), so it never has a reviewable
  // proposal to hand to <ProposalTable>. ──────────────────────────────────
  const activeProposal: (PEProposal & { items: ProposedJobItem[] }) | null =
    mode === "ai"
      ? proposalDone
        ? (propose.proposal as PEProposal & { items: ProposedJobItem[] })
        : null
      : mode === "converter"
        ? converterProposal
        : null;

  const handleProposalRejected = useCallback(() => {
    if (mode === "ai") propose.reset();
    if (mode === "converter") setConverterProposal(null);
  }, [mode, propose]);

  return (
    <div className="glass-card rounded-2xl p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-brand-400" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-white">
            Create Pipeline Mapping
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close mapping wizard"
          className="p-1.5 text-white/40 hover:text-white/70 hover:bg-white/10 rounded transition-colors"
        >
          <X size={14} aria-hidden="true" />
        </button>
      </div>

      {/* Mode tabs */}
      <div
        className="flex items-center gap-1 border-b border-white/10 pb-3 flex-wrap"
        role="tablist"
        aria-label="Mapping creation mode"
      >
        {MODE_TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={mode === id}
            onClick={() => handleModeChange(id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              mode === id
                ? "bg-brand-500/20 text-brand-300 border border-brand-500/30"
                : "text-white/50 hover:text-white/80 hover:bg-white/5 border border-transparent"
            }`}
          >
            <Icon size={13} aria-hidden="true" />
            {label}
          </button>
        ))}
      </div>

      {/* ── Mode: AI Propose ─────────────────────────────────────────────── */}
      {mode === "ai" && (
        <div className="space-y-4">
          {/* Step 1: Table name filter */}
          {!introspect.loading && !introspectDone && (
            <div className="space-y-3">
              <div>
                <label htmlFor="iw-table-names" className={LABEL_CLASS}>
                  Table names to introspect (optional, comma-separated)
                </label>
                <input
                  id="iw-table-names"
                  type="text"
                  className={INPUT_CLASS}
                  placeholder="e.g. orders, customers (leave blank for all)"
                  value={tableNamesRaw}
                  onChange={(e) => setTableNamesRaw(e.target.value)}
                />
                <p className="text-xs text-white/30 mt-1">
                  Leave blank to introspect all source tables.
                </p>
              </div>

              {introspect.error !== null && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                  <p className="text-red-400 text-xs" role="alert">
                    {introspect.error}
                  </p>
                  <button
                    type="button"
                    onClick={() => handleModeChange("ai")}
                    className="text-xs text-red-300 underline hover:no-underline mt-1"
                  >
                    Reset and try again
                  </button>
                </div>
              )}

              <button
                type="button"
                onClick={() => void handleIntrospect()}
                disabled={introspect.loading}
                className="flex items-center gap-1.5 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Search size={13} aria-hidden="true" />
                Introspect Source
              </button>
            </div>
          )}

          {/* Step 2: Introspecting spinner */}
          {introspect.loading && (
            <div className="flex items-center gap-3 py-4">
              <Loader2
                size={18}
                className="animate-spin text-brand-400 flex-shrink-0"
                aria-hidden="true"
              />
              <span className="text-sm text-white/60">
                Introspecting source schema…
              </span>
            </div>
          )}

          {/* Step 3: Schema tree + propose form */}
          {introspectDone && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-white/50">
                  Source Schema — {schemaTables.length} table
                  {schemaTables.length !== 1 ? "s" : ""} found
                </span>
                <button
                  type="button"
                  onClick={() => handleModeChange("ai")}
                  className="text-xs text-white/30 hover:text-white/60 transition-colors"
                >
                  Start over
                </button>
              </div>

              {/* Schema tree — names/types only, zero data values */}
              <div className="space-y-1.5 max-h-64 overflow-y-auto custom-scrollbar pr-1">
                {schemaTables.length === 0 ? (
                  <p className="text-xs text-white/30 text-center py-4">
                    No tables returned from introspection.
                  </p>
                ) : (
                  schemaTables.map((table) => (
                    <SchemaTableTree key={table.name} table={table} />
                  ))
                )}
              </div>

              {/* Proposal form */}
              {!propose.loading && !proposalDone && (
                <div className="space-y-3 border-t border-white/10 pt-4">
                  <div>
                    <label htmlFor="iw-user-prompt" className={LABEL_CLASS}>
                      Describe your intent for job proposals{" "}
                      <span className="text-white/30">(max 4000 chars)</span>
                    </label>
                    <textarea
                      id="iw-user-prompt"
                      rows={3}
                      maxLength={4000}
                      className={`${INPUT_CLASS} resize-y`}
                      placeholder="e.g. Create reporting layer jobs to track customer order history and product revenue"
                      value={userPrompt}
                      onChange={(e) => setUserPrompt(e.target.value)}
                    />
                    {promptError !== null && (
                      <p className="text-red-400 text-xs mt-1" role="alert">
                        {promptError}
                      </p>
                    )}
                  </div>

                  {propose.error !== null && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                      <p className="text-red-400 text-xs" role="alert">
                        {propose.error}
                      </p>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => void handlePropose()}
                    disabled={propose.loading}
                    className="flex items-center gap-1.5 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Sparkles size={13} aria-hidden="true" />
                    Propose Jobs with AI
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Proposal generating spinner */}
          {propose.loading && (
            <div className="flex items-center gap-3 py-4">
              <Loader2
                size={18}
                className="animate-spin text-brand-400 flex-shrink-0"
                aria-hidden="true"
              />
              <span className="text-sm text-white/60">
                Generating job proposals…
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Mode: Upload Mapping (manual CSV/XLSX) ─────────────────────────── */}
      {mode === "upload" && uploadResult === null && (
        <div className="space-y-3">
          <p className="text-xs text-white/50">
            Upload a CSV or XLSX mapping sheet — either the canonical columns
            (job_name, target_table, target_layer, source_table, source_column,
            target_column, data_type, is_pk, load_strategy) or a Data Converter
            &quot;Generate STTM&quot; export (Source Table, Source Column,
            Target Object (SF), Target Field, ...) are both supported. Jobs are
            created automatically once the file parses successfully.
          </p>
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            aria-label="Mapping sheet file"
            onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
            className="block w-full text-xs text-white/60 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-white/10 file:text-white/70 hover:file:bg-white/20"
          />
          {uploadError !== null && (
            <p className="text-red-400 text-xs" role="alert">
              {uploadError}
            </p>
          )}
          <button
            type="button"
            onClick={() => void handleUpload()}
            disabled={uploadLoading || uploadFile === null}
            className="flex items-center gap-1.5 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploadLoading ? (
              <Loader2 size={13} className="animate-spin" aria-hidden="true" />
            ) : (
              <Upload size={13} aria-hidden="true" />
            )}
            Upload & Create Jobs
          </button>
        </div>
      )}

      {mode === "upload" && uploadResult !== null && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-1">
          <p className="text-xs text-emerald-300 font-medium">
            ✓ Mapping imported
          </p>
          <p className="text-xs text-white/60">
            {uploadResult.jobsCreated} job
            {uploadResult.jobsCreated !== 1 ? "s" : ""} created
            {uploadResult.jobsSkipped > 0
              ? `, ${uploadResult.jobsSkipped} skipped (duplicate job name)`
              : ""}
            .
          </p>
          <button
            type="button"
            onClick={() => {
              setUploadResult(null);
              setUploadFile(null);
            }}
            className="text-xs text-white/30 hover:text-white/60 transition-colors"
          >
            Upload another file
          </button>
        </div>
      )}

      {/* ── Mode: From Data Converter (reuse a finalized STTM) ─────────────── */}
      {mode === "converter" && converterProposal === null && (
        <div className="space-y-3">
          <p className="text-xs text-white/50">
            Reuse a source-to-target mapping already finalized in Data Converter
            for a project.
          </p>
          <div>
            <label htmlFor="iw-converter-project" className={LABEL_CLASS}>
              Data Converter project
            </label>
            <select
              id="iw-converter-project"
              className={INPUT_CLASS}
              value={selectedConverterProjectId}
              onChange={(e) => setSelectedConverterProjectId(e.target.value)}
              disabled={converterProjectsLoading}
            >
              <option value="">
                {converterProjectsLoading
                  ? "Loading projects…"
                  : "Select a project…"}
              </option>
              {converterProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          {selectedConverterProjectId !== "" && (
            <div>
              <label htmlFor="iw-converter-version" className={LABEL_CLASS}>
                Mapping version
              </label>
              <select
                id="iw-converter-version"
                className={INPUT_CLASS}
                value={selectedHistoryId}
                onChange={(e) => setSelectedHistoryId(e.target.value)}
                disabled={
                  converterVersionsLoading || converterVersions.length === 0
                }
              >
                {converterVersionsLoading && (
                  <option value="">Loading saved versions…</option>
                )}
                {!converterVersionsLoading &&
                  converterVersions.length === 0 && (
                    <option value="">No saved mappings found</option>
                  )}
                {converterVersions.map((v, i) => (
                  <option key={v.historyId} value={v.historyId}>
                    {i === 0 ? "Latest — " : ""}
                    {new Date(v.savedAt).toLocaleString()} ({v.tableCount} table
                    {v.tableCount !== 1 ? "s" : ""})
                  </option>
                ))}
              </select>
            </div>
          )}
          {converterError !== null && (
            <p className="text-red-400 text-xs" role="alert">
              {converterError}
            </p>
          )}
          <button
            type="button"
            onClick={() => void handleReuseFromConverter()}
            disabled={converterLoading || !selectedConverterProjectId}
            className="flex items-center gap-1.5 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {converterLoading ? (
              <Loader2 size={13} className="animate-spin" aria-hidden="true" />
            ) : (
              <FolderInput size={13} aria-hidden="true" />
            )}
            Reuse Mapping
          </button>
        </div>
      )}

      {/* ── Unified proposal review — shared by AI Propose, Auto-Map, and From
           Data Converter (Upload Mapping auto-creates jobs directly, see
           handleUpload, and never reaches this). ─────────────────────────── */}
      {activeProposal !== null && (
        <ProposalTable
          key={activeProposal.proposalId}
          proposal={activeProposal}
          onAllAccepted={onJobsAccepted}
          onRejected={handleProposalRejected}
        />
      )}
    </div>
  );
}
